import React, { useMemo, useState } from "react";
import axios from "axios";
import TmdbLookupModal from "@/components/TmdbLookupModal";

/**
 * MEGA ingest panel — two modes:
 *   • SINGLE: paste one iframe/URL + optional title.
 *   • BULK:   paste many iframes/URLs (any mix of formats, separated
 *             by newlines OR commas), preview the parsed list with
 *             editable titles, then submit them all in one request.
 *             Bad rows are reported back and skipped; good rows land.
 *
 * Warning: MEGA plays through their embed with their own controls, so
 * the remote play/pause + watch-progress tracking don't apply to MEGA
 * tapes. Same trade-offs as archive.org.
 */

// Split a paste blob into candidate rows. Owners can drop:
//   • line-separated iframe snippets
//   • line-separated URLs
//   • comma-separated URLs
//   • a mix of the above
// We split on newlines FIRST (so multi-line iframes stay intact if
// possible), then only split on commas for rows that don't look like
// an iframe tag — otherwise "<iframe ..., width=..." would be shredded.
function splitBulkInput(raw) {
  if (!raw) return [];
  const lines = raw.split(/\r?\n+/).map((s) => s.trim()).filter(Boolean);
  const out = [];
  for (const line of lines) {
    if (/<iframe/i.test(line)) {
      out.push(line);
    } else {
      for (const chunk of line.split(",").map((s) => s.trim()).filter(Boolean)) {
        out.push(chunk);
      }
    }
  }
  return out;
}

// Client-side parser mirroring the backend regexes. We only need it to
// (a) preview which rows look valid before submit and (b) generate a
// sensible auto-title placeholder in the review list. The backend is
// still the source of truth on insert.
const MEGA_EMBED_RE = /https?:\/\/(?:www\.)?mega\.nz\/embed\/([A-Za-z0-9_-]+)(#[A-Za-z0-9_!-]+)?/i;
const MEGA_SHARE_RE = /https?:\/\/(?:www\.)?mega\.nz\/file\/([A-Za-z0-9_-]+)(#[A-Za-z0-9_!-]+)?/i;

function parseMegaRow(raw) {
  const text = (raw || "").trim();
  if (!text) return { ok: false, id: null };
  let m = text.match(MEGA_EMBED_RE);
  if (m) return { ok: true, id: m[1] };
  m = text.match(MEGA_SHARE_RE);
  if (m) return { ok: true, id: m[1] };
  return { ok: false, id: null };
}

// Same extension list the backend strips at ingest — kept in sync so
// the client-side "STRIP EXTENSIONS" button in the review list gives
// identical results to what the server would produce on submit anyway.
const VIDEO_EXT_RE = /\.(mp4|mkv|avi|mov|webm|m4v|wmv|flv|mpg|mpeg|ts|m2ts|3gp|ogv)\.?\s*$/i;
const stripVideoExt = (s) => (s || "").replace(VIDEO_EXT_RE, "").trimEnd();

export default function MegaIngest({
  apiBase,
  authHeaders,
  channels,
  selectedChannel,
  busy,
  setBusy,
  append,
  onUploaded,
  onTuneToChannel,
}) {
  const [mode, setMode] = useState("single"); // "single" | "bulk"

  // --- SINGLE mode state ---
  const [embed, setEmbed] = useState("");
  const [title, setTitle] = useState("");

  // --- BULK mode state ---
  // `bulkRaw` is the paste blob; `rows` is the parsed/review list (null
  // until the owner hits "Parse & review"). Each row: {raw, ok, autoTitle, title}.
  const [bulkRaw, setBulkRaw] = useState("");
  const [rows, setRows] = useState(null);

  // Sub-channels are frontend-derived — ingesting a tape onto one has
  // to write against the parent's DB row (the sub id 404s). All ingest
  // endpoints resolve `effectiveChannelId` so a MEGA/YT/Archive add
  // done while a sub is selected simply drops the tape onto the parent
  // channel. The tape then re-appears in the sub automatically once
  // its `show_title` matches.
  const selectedCh = channels.find((c) => c.id === selectedChannel);
  const isSubChannel = !!selectedCh?.isSubChannel;
  const effectiveChannelId = isSubChannel ? selectedCh.parent_id : selectedChannel;
  const chNumber = channels.find((c) => c.id === effectiveChannelId)?.number ?? "?";

  const handleAdd = async () => {
    if (!selectedChannel || !embed.trim()) {
      append("> ERROR: SELECT A CHANNEL AND PASTE A MEGA IFRAME OR URL");
      return;
    }
    setBusy(true);
    append(`> LINKING MEGA TAPE TO CH ${chNumber}...`);
    try {
      await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/mega`,
        { embed: embed.trim(), title: title.trim() || null },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append("> MEGA TAPE LINKED.");
      setEmbed("");
      setTitle("");
      await onUploaded(effectiveChannelId);
      if (onTuneToChannel) onTuneToChannel(effectiveChannelId);
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleParseBulk = () => {
    const parts = splitBulkInput(bulkRaw);
    if (!parts.length) {
      append("> ERROR: PASTE ONE OR MORE MEGA LINKS TO PARSE");
      return;
    }
    const parsed = parts.map((raw) => {
      const p = parseMegaRow(raw);
      const autoTitle = p.ok ? `MEGA — ${p.id}` : "";
      return { raw, ok: p.ok, autoTitle, title: "" };
    });
    setRows(parsed);
    const okCount = parsed.filter((r) => r.ok).length;
    append(`> PARSED ${parsed.length} ROW(S) — ${okCount} VALID, ${parsed.length - okCount} SKIPPED`);
  };

  const validCount = useMemo(
    () => (rows ? rows.filter((r) => r.ok).length : 0),
    [rows]
  );

  const handleBulkSubmit = async () => {
    if (!selectedChannel) {
      append("> ERROR: SELECT A CHANNEL FIRST");
      return;
    }
    const items = (rows || [])
      .filter((r) => r.ok)
      .map((r) => ({ embed: r.raw, title: (r.title || "").trim() || null }));
    if (!items.length) {
      append("> ERROR: NO VALID ROWS TO ADD");
      return;
    }
    setBusy(true);
    append(`> BULK LINKING ${items.length} MEGA TAPE(S) TO CH ${chNumber}...`);
    try {
      const { data } = await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/mega/bulk`,
        { items },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      const addedN = (data?.added || []).length;
      const failedN = (data?.failed || []).length;
      append(`> DONE — ${addedN} ADDED, ${failedN} SKIPPED`);
      for (const f of data?.failed || []) {
        append(`>   SKIP row ${f.index + 1}: ${f.reason}`);
      }
      // Reset input on any success; keep raw paste if nothing landed
      // so the owner can retry after fixing rows.
      if (addedN > 0) {
        setBulkRaw("");
        setRows(null);
        await onUploaded(effectiveChannelId);
        if (onTuneToChannel) onTuneToChannel(effectiveChannelId);
      }
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleResetBulk = () => {
    setRows(null);
  };

  // Bulk strip video-container extensions from all TYPED titles in the
  // review list. Client-side only — for auto-titles (title blank) the
  // backend already strips at ingest, so this only touches rows the
  // owner already typed something into. Keeps the panel behaviour
  // consistent regardless of where the .mp4 originated.
  const stripReviewExtensions = () => {
    setRows((prev) => (prev || []).map((r) => (
      r.ok && r.title ? { ...r, title: stripVideoExt(r.title) } : r
    )));
  };

  // Owner-only channel-wide cleanup — hits the strip-extensions
  // endpoint for the currently-selected channel. Rewrites every live
  // MEGA tape's title on that channel so historical .mp4 tails go
  // away in one click. Idempotent so it can be re-run safely.
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const handleChannelCleanup = async () => {
    if (!selectedChannel || cleanupBusy) return;
    setCleanupBusy(true);
    append(`> SCRUBBING EXTENSIONS FROM MEGA TAPES ON CH ${chNumber}...`);
    try {
      const { data } = await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/mega/strip-extensions`,
        {},
        { headers: authHeaders() }
      );
      append(`> DONE — SCANNED ${data.scanned}, UPDATED ${data.updated}, UNCHANGED ${data.unchanged}`);
      if (data.updated > 0) await onUploaded(effectiveChannelId);
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCleanupBusy(false);
    }
  };

  // Owner-only bulk TMDb rescan — runs `title_lookup()` across every
  // MEGA tape on the selected channel (or the whole library when the
  // owner unticks "this channel only"). Backfills posters + synopses
  // that pre-date the auto-attach feature or ingests that missed a
  // match at add-time. Safe to re-run: tapes already carrying a
  // `poster_url` are skipped unless `overwrite=true`.
  const [rescanBusy, setRescanBusy] = useState(false);
  const [rescanScope, setRescanScope] = useState("channel"); // "channel" | "all"
  // "🔍 UNMATCHED" triage list — every MEGA tape still missing a
  // poster_url after the last rescan. Owner opens the panel, clicks
  // 🎬 on any row to launch the per-tape TMDb picker.
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);
  const [unmatchedRows, setUnmatchedRows] = useState([]);
  const [unmatchedBusy, setUnmatchedBusy] = useState(false);
  const [unmatchedScope, setUnmatchedScope] = useState("all"); // "all" | "channel"
  const [tmdbTapeTarget, setTmdbTapeTarget] = useState(null); // {video_id, title, channel_id}
  // "APPLY SHOW TO ALL UNMATCHED ON CH X" bulk-apply flow. When set,
  // the shared TMDb modal opens in override mode — a pick applies the
  // show's poster/synopsis to every unmatched MEGA tape on the
  // targeted channel via one round-trip.
  const [tmdbShowChannel, setTmdbShowChannel] = useState(null); // {channel_id, name, number, count}

  const loadUnmatched = async (scope = unmatchedScope) => {
    setUnmatchedBusy(true);
    try {
      const params = scope === "channel" && effectiveChannelId
        ? `?channel_id=${encodeURIComponent(effectiveChannelId)}`
        : "";
      const { data } = await axios.get(`${apiBase}/tmdb/unmatched${params}`, {
        headers: authHeaders(),
      });
      setUnmatchedRows(data.rows || []);
      setRenameDrafts({});
      append(`> UNMATCHED — ${data.total} MEGA TAPES WITHOUT A POSTER`);
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUnmatchedBusy(false);
    }
  };

  // Draft state for the inline title-rename inputs — keyed by video id.
  // Bulk "⚡ SAVE ALL & RESCAN" commits every dirty draft in one shot
  // then runs a single RE-SCAN pass so operators fixing 20+ typos
  // never have to click SAVE per row.
  const [saveAllBusy, setSaveAllBusy] = useState(false);
  const dirtyDraftEntries = () =>
    unmatchedRows
      .map((r) => {
        const draft = renameDrafts[r.video_id];
        if (draft == null) return null;
        const trimmed = draft.trim();
        if (!trimmed || trimmed === r.title.trim()) return null;
        return { row: r, newTitle: trimmed };
      })
      .filter(Boolean);

  const handleSaveAllAndRescan = async () => {
    const dirty = dirtyDraftEntries();
    if (dirty.length === 0) {
      append("> NO PENDING RENAMES — nothing to save.");
      return;
    }
    setSaveAllBusy(true);
    append(`> SAVING ${dirty.length} RENAMES...`);
    let failed = 0;
    // PATCH renames in parallel — the /api/videos/{id} endpoint is
    // idempotent and DB-safe under concurrent writes for different ids.
    const patchResults = await Promise.allSettled(
      dirty.map(({ row, newTitle }) =>
        axios.patch(
          `${apiBase}/videos/${row.video_id}`,
          { title: newTitle },
          { headers: authHeaders() },
        ),
      ),
    );
    failed = patchResults.filter((r) => r.status === "rejected").length;
    append(`> RENAMED ${dirty.length - failed}/${dirty.length}${failed ? ` (${failed} failed)` : ""} — RUNNING RE-SCAN...`);
    try {
      const { data } = await axios.post(
        `${apiBase}/tmdb/rescan-mega`,
        // Rescan the whole library — even if all dirty rows lived on
        // one channel, sibling tapes on other channels might have
        // benefited from concurrent library work. Cheap because the
        // TMDb LRU short-circuits duplicate lookups.
        { overwrite: false },
        { headers: authHeaders() },
      );
      append(`> DONE — SCANNED ${data.total}, MATCHED ${data.matched}, SKIPPED ${data.skipped}, NO-MATCH ${data.unmatched}`);
    } catch (e) {
      append(`> RE-SCAN ERROR: ${e.response?.data?.detail || e.message}`);
    }
    // Refresh the channel currently in view so the guide picks up new
    // posters immediately.
    if (selectedChannel) await onUploaded(effectiveChannelId);
    await loadUnmatched();
    setSaveAllBusy(false);
  };

  const [renameDrafts, setRenameDrafts] = useState({});
  const saveRename = async (row) => {
    const draft = renameDrafts[row.video_id];
    if (draft == null || draft.trim() === "" || draft.trim() === row.title.trim()) return;
    setUnmatchedBusy(true);
    try {
      await axios.patch(
        `${apiBase}/videos/${row.video_id}`,
        { title: draft.trim() },
        { headers: authHeaders() },
      );
      try {
        const s = await axios.get(
          `${apiBase}/tmdb/search?q=${encodeURIComponent(draft.trim())}`,
          { headers: authHeaders() },
        );
        const top = s.data?.results?.[0];
        if (top) {
          await axios.post(
            `${apiBase}/tmdb/apply/video/${row.video_id}`,
            {
              tmdb_id: top.tmdb_id,
              media_type: top.media_type,
              apply_poster: true,
              apply_description: true,
            },
            { headers: authHeaders() },
          );
          append(`> ✔ RENAMED + MATCHED — ${draft.trim()} → ${top.title}`);
        } else {
          append(`> RENAMED (no TMDb hit yet) — ${draft.trim()}`);
        }
      } catch (e) {
        append(`> RENAMED (TMDb auto-lookup failed) — ${draft.trim()}`);
      }
      if (row.channel_id) await onUploaded(row.channel_id);
      await loadUnmatched();
    } catch (e) {
      append(`> ERROR RENAMING: ${e.response?.data?.detail || e.message}`);
      setUnmatchedBusy(false);
    }
  };

  const handleTmdbRescan = async () => {
    if (rescanBusy) return;
    if (rescanScope === "channel" && !effectiveChannelId) return;
    setRescanBusy(true);
    const label = rescanScope === "all" ? "EVERY MEGA TAPE" : `CH ${chNumber}`;
    append(`> TMDB RESCAN — ${label}...`);
    try {
      const { data } = await axios.post(
        `${apiBase}/tmdb/rescan-mega`,
        rescanScope === "all"
          ? { overwrite: false }
          : { channel_id: effectiveChannelId, overwrite: false },
        { headers: authHeaders() }
      );
      append(`> DONE — SCANNED ${data.total}, MATCHED ${data.matched}, SKIPPED ${data.skipped}, NO-MATCH ${data.unmatched}`);
      if (data.matched > 0) {
        if (rescanScope === "all") {
          // Refresh whatever channel is currently in view so the guide
          // updates without a page reload.
          if (effectiveChannelId) await onUploaded(effectiveChannelId);
        } else {
          await onUploaded(effectiveChannelId);
        }
      }
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRescanBusy(false);
    }
  };

  return (
    <div className="border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2" data-testid="mega-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] opacity-80">&gt; LINK A MEGA TAPE (NO UPLOAD):</div>
        <div className="flex gap-1" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "single"}
            onClick={() => setMode("single")}
            data-testid="mega-mode-single"
            className={`text-[10px] px-1.5 py-0.5 border tracking-widest ${
              mode === "single"
                ? "bg-[var(--phosphor)] text-black border-[var(--phosphor)]"
                : "border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"
            }`}
          >
            SINGLE
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "bulk"}
            onClick={() => setMode("bulk")}
            data-testid="mega-mode-bulk"
            className={`text-[10px] px-1.5 py-0.5 border tracking-widest ${
              mode === "bulk"
                ? "bg-[var(--phosphor)] text-black border-[var(--phosphor)]"
                : "border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"
            }`}
          >
            BULK
          </button>
        </div>
      </div>

      {/* Channel-wide extension cleanup — one-click strip of trailing
          `.mp4`/`.mkv`/`.avi`/... from every existing MEGA tape title
          on the selected channel. Fixes tapes ingested before the
          server-side auto-strip existed. Idempotent — safe to re-run.
          Sits above both mode contents so it's always reachable. */}
      <div className="flex items-center gap-2 mb-2 border-b border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] pb-2 flex-wrap">
        <span className="text-[9px] opacity-60 tracking-widest shrink-0">CLEAN UP EXISTING:</span>
        <button
          onClick={handleChannelCleanup}
          disabled={!selectedChannel || cleanupBusy || busy}
          data-testid="mega-channel-strip-ext"
          title="Remove .mp4 / .mkv / .avi / .mov / .webm from every MEGA tape title on this channel"
          className="text-[10px] px-2 py-0.5 border border-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 tracking-widest"
        >
          {cleanupBusy ? "SCRUBBING…" : `STRIP .MP4 / .MKV FROM CH ${chNumber}`}
        </button>
        {/* TMDb bulk backfill — matches every MEGA tape's title against
            TheMovieDB and fills poster_url + description + year on any
            hit that doesn't already have artwork. Owner picks whether
            to scope to the current channel (default) or run against
            the whole library. */}
        <span className="text-[9px] opacity-60 tracking-widest shrink-0 ml-2">TMDB BACKFILL:</span>
        <select
          value={rescanScope}
          onChange={(e) => setRescanScope(e.target.value)}
          disabled={rescanBusy || busy}
          data-testid="tmdb-rescan-scope"
          className="text-[10px] bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-1 py-0.5 outline-none tracking-widest"
        >
          <option value="channel">CH {chNumber}</option>
          <option value="all">ALL CHANNELS</option>
        </select>
        <button
          onClick={handleTmdbRescan}
          disabled={rescanBusy || busy || (rescanScope === "channel" && !selectedChannel)}
          data-testid="tmdb-rescan-mega"
          title="Fill poster + synopsis on every MEGA tape that doesn't already have them (from TheMovieDB)"
          className="text-[10px] px-2 py-0.5 border border-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 tracking-widest"
        >
          {rescanBusy ? "SCANNING…" : "🎬 RE-SCAN"}
        </button>
        {/* One-shot migration: walk every channel whose thumbnail_url
            currently sits on `image.tmdb.org/t/p/w500/` (portrait
            posters saved before the 16:9 backdrop fix landed) and
            swap them for the correct backdrop_url. Non-TMDb channel
            thumbs are ignored — safe to hammer. */}
        <button
          onClick={async () => {
            if (rescanBusy || busy) return;
            setRescanBusy(true);
            append(`> REFRESHING CHANNEL THUMBS FROM TMDB...`);
            try {
              const { data } = await axios.post(
                `${apiBase}/tmdb/refresh-channel-thumbs`,
                {},
                { headers: authHeaders() },
              );
              append(`> DONE — TMDB CHANNELS ${data.total}, REFRESHED ${data.refreshed}, ALREADY 16:9 ${data.already_backdrop}, UNMATCHED ${data.unmatched}`);
              if (effectiveChannelId) await onUploaded(effectiveChannelId);
            } catch (e) {
              append(`> ERROR: ${e.response?.data?.detail || e.message}`);
            } finally {
              setRescanBusy(false);
            }
          }}
          disabled={rescanBusy || busy}
          data-testid="tmdb-refresh-channel-thumbs"
          title="Swap any pre-fix portrait TMDb channel thumbnails to landscape 16:9 backdrops — one shot for the whole library"
          className="text-[10px] px-2 py-0.5 border border-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 tracking-widest"
        >
          🔄 REFRESH CH THUMBS
        </button>
        <button
          onClick={() => {
            const nextOpen = !unmatchedOpen;
            setUnmatchedOpen(nextOpen);
            if (nextOpen) loadUnmatched();
          }}
          disabled={busy}
          data-testid="tmdb-unmatched-toggle"
          title="Show every MEGA tape still missing a poster after RE-SCAN — usually filename typos"
          className="text-[10px] px-2 py-0.5 border border-[#FFAA33]/60 text-[#FFAA33] hover:bg-[#FFAA33] hover:text-black transition-colors disabled:opacity-40 tracking-widest"
        >
          {unmatchedOpen ? "▲ UNMATCHED" : "🔍 UNMATCHED"}
        </button>
      </div>

      {unmatchedOpen && (
        <div
          className="mb-2 border border-[#FFAA33]/40 p-2 text-[10px]"
          data-testid="tmdb-unmatched-panel"
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="opacity-70 tracking-widest">SCOPE:</span>
            <select
              value={unmatchedScope}
              onChange={(e) => { setUnmatchedScope(e.target.value); loadUnmatched(e.target.value); }}
              className="bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-1 py-0.5 outline-none tracking-widest"
              data-testid="tmdb-unmatched-scope"
            >
              <option value="all">ALL CHANNELS</option>
              <option value="channel">CH {chNumber}</option>
            </select>
            <button
              onClick={() => loadUnmatched()}
              disabled={unmatchedBusy}
              className="px-2 py-0.5 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
              data-testid="tmdb-unmatched-refresh"
            >
              {unmatchedBusy ? "…" : "REFRESH"}
            </button>
            <button
              onClick={handleSaveAllAndRescan}
              disabled={saveAllBusy || unmatchedBusy || dirtyDraftEntries().length === 0}
              className="px-2 py-0.5 border border-[#FFAA33] text-[#FFAA33] hover:bg-[#FFAA33] hover:text-black transition-colors disabled:opacity-40"
              data-testid="tmdb-unmatched-save-all"
              title="Save every pending rename in one shot, then re-run the TMDb rescan"
            >
              {saveAllBusy ? "…" : `⚡ SAVE ALL${dirtyDraftEntries().length ? ` (${dirtyDraftEntries().length})` : ""}`}
            </button>
            {/* Bulk "apply one show to every unmatched tape on this
                channel" — perfect for a 26-episode series where none
                of the raw filenames match. Owner scopes to a channel
                (via the SCOPE selector or the currently-selected
                channel from the terminal), clicks 🎬 APPLY SHOW,
                picks the correct show once, and every unmatched tape
                on that channel inherits the same poster + synopsis. */}
            {selectedChannel && (
              <button
                onClick={() => setTmdbShowChannel({
                  channel_id: effectiveChannelId,
                  name: channels.find((c) => c.id === effectiveChannelId)?.name || "",
                  number: chNumber,
                  count: unmatchedRows.filter((r) => r.channel_id === effectiveChannelId).length,
                })}
                disabled={unmatchedBusy || unmatchedRows.filter((r) => r.channel_id === effectiveChannelId).length === 0}
                className="px-2 py-0.5 border border-[#FFAA33] text-[#FFAA33] hover:bg-[#FFAA33] hover:text-black transition-colors disabled:opacity-40"
                data-testid="tmdb-apply-show-to-channel"
                title="Pick one show in TMDb → apply its poster + synopsis to every unmatched MEGA tape on this channel (great for 26-episode series where the filenames don't match)"
              >
                🎬 APPLY SHOW TO CH {chNumber}
              </button>
            )}
            <span className="opacity-70 ml-auto">{unmatchedRows.length} pending</span>
          </div>
          {unmatchedRows.length === 0 ? (
            <div className="text-center opacity-60 py-2 tracking-widest">
              {unmatchedBusy ? "LOADING…" : "★ ALL MEGA TAPES ARE MATCHED ★"}
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto divide-y divide-[color-mix(in_srgb,var(--phosphor)_18%,transparent)]">
              {unmatchedRows.map((r) => {
                const draftKey = r.video_id;
                const draftVal = renameDrafts[draftKey];
                const currentVal = draftVal != null ? draftVal : (r.suggested_query || r.title);
                const dirty = draftVal != null && draftVal.trim() !== r.title.trim() && draftVal.trim() !== "";
                return (
                <div
                  key={r.video_id}
                  className="flex items-center gap-2 py-1 px-1 hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)]"
                  data-testid={`tmdb-unmatched-row-${r.video_id}`}
                >
                  <span className="tabular-nums opacity-60 shrink-0 w-8 text-right">
                    {r.channel_number ? `#${r.channel_number}` : "—"}
                  </span>
                  <span className="opacity-60 tracking-widest shrink-0 max-w-[6rem] truncate" title={r.channel_name}>
                    {r.channel_name}
                  </span>
                  {/* Inline title editor. Backed by `renameDrafts` so
                      per-row edits don't blow away sibling rows.
                      Enter or blur commits; Escape reverts. On save
                      the tape is PATCHed, then we auto-run a TMDb
                      lookup — if it hits, the row falls off the pile
                      on the next reload. */}
                  <input
                    type="text"
                    value={currentVal}
                    onChange={(e) => setRenameDrafts((d) => ({ ...d, [draftKey]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); saveRename(r); }
                      else if (e.key === "Escape") {
                        setRenameDrafts((d) => ({ ...d, [draftKey]: r.suggested_query || r.title }));
                      }
                    }}
                    placeholder={r.title}
                    title={`Original: ${r.title}`}
                    data-testid={`tmdb-unmatched-rename-${r.video_id}`}
                    className={`flex-1 min-w-0 bg-black px-1 py-0.5 border outline-none ${
                      dirty
                        ? "border-[#FFAA33]"
                        : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]"
                    }`}
                  />
                  <button
                    onClick={() => saveRename(r)}
                    disabled={!dirty || unmatchedBusy}
                    data-testid={`tmdb-unmatched-save-${r.video_id}`}
                    title="Save rename and auto-match against TMDb"
                    className="text-[10px] px-1.5 py-0.5 border transition-colors shrink-0 disabled:opacity-40 border-[#FFAA33]/70 text-[#FFAA33] hover:bg-[#FFAA33] hover:text-black"
                  >
                    ✔ SAVE
                  </button>
                  <button
                    onClick={() => setTmdbTapeTarget(r)}
                    className="text-[10px] px-1.5 py-0.5 border border-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors shrink-0"
                    title="Open the TMDb picker for this tape (manual browse)"
                    data-testid={`tmdb-unmatched-fix-${r.video_id}`}
                  >
                    🎬 FIX
                  </button>
                </div>
              );})}
            </div>
          )}
        </div>
      )}

      <TmdbLookupModal
        open={!!tmdbTapeTarget}
        initialQuery={tmdbTapeTarget?.suggested_query || tmdbTapeTarget?.title || ""}
        initialYear={tmdbTapeTarget?.suggested_year || null}
        mode="video"
        targetId={tmdbTapeTarget?.video_id}
        apiBase={apiBase}
        authHeaders={authHeaders}
        onClose={() => setTmdbTapeTarget(null)}
        onApplied={async () => {
          if (tmdbTapeTarget?.channel_id) await onUploaded(tmdbTapeTarget.channel_id);
          await loadUnmatched();
        }}
      />

      {/* Bulk-apply modal — same picker, different apply endpoint.
          `applyOverride` intercepts the modal's submit and POSTs to
          /tmdb/apply-show-to-channel-episodes with the picked show +
          the target channel so every unmatched tape on that channel
          inherits the same poster + synopsis in one call. */}
      <TmdbLookupModal
        open={!!tmdbShowChannel}
        initialQuery={tmdbShowChannel?.name || ""}
        mode="video"
        targetId={null}
        apiBase={apiBase}
        authHeaders={authHeaders}
        applyLabel={tmdbShowChannel ? `APPLY TO ${tmdbShowChannel.count} EPISODES` : "APPLY"}
        applyOverride={async ({ picked, applyPoster, applyDescription }) => {
          const res = await fetch(`${apiBase}/tmdb/apply-show-to-channel-episodes`, {
            method: "POST",
            headers: { ...authHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify({
              channel_id: tmdbShowChannel.channel_id,
              tmdb_id: picked.tmdb_id,
              media_type: picked.media_type,
              apply_poster: applyPoster,
              apply_description: applyDescription,
              unmatched_only: true,
            }),
          });
          if (res.ok) {
            // Clone first — the modal's applyPick handler will also
            // `res.json()` this response after we return. Reading the
            // body twice throws "body stream already read".
            try {
              const data = await res.clone().json();
              append(`> ✔ APPLIED "${data.show_title || picked.title}" TO ${data.updated || 0}/${data.total || 0} EPISODES ON CH ${tmdbShowChannel.number}`);
            } catch { /* logging is best-effort */ }
          }
          return res;
        }}
        onClose={() => setTmdbShowChannel(null)}
        onApplied={async () => {
          if (tmdbShowChannel?.channel_id) await onUploaded(tmdbShowChannel.channel_id);
          await loadUnmatched();
        }}
      />

      {mode === "single" && (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={embed}
            onChange={(e) => setEmbed(e.target.value)}
            rows={3}
            placeholder={'<iframe ... src="https://mega.nz/embed/..." ...></iframe>\nor\nhttps://mega.nz/embed/{id}#{key}\nor\nhttps://mega.nz/file/{id}#{key}'}
            data-testid="mega-embed-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1.5 text-[11px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono resize-y"
          />
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Display title (optional — auto-detected from MEGA if left blank)"
            data-testid="mega-title-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1.5 text-[11px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
          />
          <button
            onClick={handleAdd}
            disabled={busy || !embed.trim() || !selectedChannel}
            data-testid="mega-add-button"
            className="border border-[var(--phosphor)] py-1 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-bold tracking-widest"
          >
            LINK TAPE TO CH {chNumber}
          </button>
          <div className="text-[9px] opacity-60 leading-snug">
            Title auto-fetched from MEGA when possible. MEGA tapes play through
            their embed (no upload, no watermark, no auto-advance, remote
            play/pause won&apos;t control MEGA&apos;s player).
          </div>
        </div>
      )}

      {mode === "bulk" && (
        <div className="flex flex-col gap-1.5">
          {!rows && (
            <>
              <textarea
                value={bulkRaw}
                onChange={(e) => setBulkRaw(e.target.value)}
                rows={6}
                placeholder={'Paste one MEGA link per line (or comma-separated). Any mix works:\n<iframe ... src="https://mega.nz/embed/..."></iframe>\nhttps://mega.nz/embed/{id}#{key}\nhttps://mega.nz/file/{id}#{key}'}
                data-testid="mega-bulk-input"
                className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1.5 text-[11px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono resize-y"
              />
              <button
                onClick={handleParseBulk}
                disabled={busy || !bulkRaw.trim()}
                data-testid="mega-bulk-parse"
                className="border border-[var(--phosphor)] py-1 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-bold tracking-widest"
              >
                PARSE &amp; REVIEW
              </button>
              <div className="text-[9px] opacity-60 leading-snug">
                Bad rows are skipped with a reason after submit. Max 200 links
                per batch.
              </div>
            </>
          )}

          {rows && (
            <>
              <div className="text-[10px] opacity-80 flex items-center justify-between gap-2">
                <span data-testid="mega-bulk-summary">
                  {rows.length} PARSED · {validCount} VALID · {rows.length - validCount} SKIPPED
                </span>
                <div className="flex items-center gap-1">
                  {/* Strip video-container extensions from every typed
                      title in the review list. No-op for rows where
                      the owner left the title blank (backend strips at
                      ingest for those). Keeps the panel consistent
                      regardless of paste source. */}
                  <button
                    onClick={stripReviewExtensions}
                    disabled={busy}
                    data-testid="mega-bulk-strip-ext"
                    title="Remove .mp4 / .mkv / .avi / .mov / .webm from typed titles"
                    className="text-[10px] px-1.5 py-0.5 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"
                  >
                    STRIP EXT
                  </button>
                  <button
                    onClick={handleResetBulk}
                    disabled={busy}
                    data-testid="mega-bulk-reset"
                    className="text-[10px] px-1.5 py-0.5 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"
                  >
                    EDIT PASTE
                  </button>
                </div>
              </div>
              <div
                className="flex flex-col gap-1 max-h-56 overflow-y-auto scrollbar-crt border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-1"
                data-testid="mega-bulk-review-list"
              >
                {rows.map((r, i) => (
                  <div
                    key={r.url || r.link || `mega-row-${i}`}
                    className={`flex items-center gap-1 text-[10px] ${
                      r.ok ? "" : "opacity-50"
                    }`}
                    data-testid={`mega-bulk-row-${i}`}
                  >
                    <span
                      className={`shrink-0 w-4 text-center ${
                        r.ok ? "text-[var(--phosphor)]" : "text-red-400"
                      }`}
                    >
                      {r.ok ? "•" : "×"}
                    </span>
                    {r.ok ? (
                      <input
                        type="text"
                        value={r.title}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRows((prev) =>
                            prev.map((row, ri) =>
                              ri === i ? { ...row, title: v } : row
                            )
                          );
                        }}
                        placeholder={r.autoTitle}
                        data-testid={`mega-bulk-row-title-${i}`}
                        className="flex-1 min-w-0 bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] px-1 py-0.5 text-[10px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_35%,transparent)]"
                      />
                    ) : (
                      <span className="flex-1 min-w-0 truncate italic">
                        NOT A MEGA LINK — {r.raw.slice(0, 60)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={handleBulkSubmit}
                disabled={busy || validCount === 0 || !selectedChannel}
                data-testid="mega-bulk-submit"
                className="border border-[var(--phosphor)] py-1 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-bold tracking-widest"
              >
                LINK {validCount} TAPE(S) TO CH {chNumber}
              </button>
              <div className="text-[9px] opacity-60 leading-snug">
                Titles left blank auto-fetch from MEGA (or fall back to
                &quot;MEGA — {"{id}"}&quot;).
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
